import { Button } from "@/components/ui/button";
import { ArrowLeft, Settings } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";

export default function Privacy() {
  const navigate = useNavigate();
  const location = useLocation();
  const [cookiePreferences, setCookiePreferences] = useState(() => {
    const stored = localStorage.getItem("cookie-consent");
    return stored ? JSON.parse(stored) : null;
  });

  const openCookieSettings = () => {
    localStorage.removeItem("cookie-consent");
    window.location.reload();
  };

  useEffect(() => {
    if (location.hash === "#cookies") {
      setTimeout(() => {
        const element = document.getElementById("cookies");
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-background">
      <div className="container max-w-4xl mx-auto py-12 px-4 space-y-8">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Zurück
        </Button>

        <div className="space-y-6">
          <div>
            <h1 className="text-4xl font-bold mb-2">Datenschutzerklärung</h1>
            <p className="text-muted-foreground">Zuletzt aktualisiert: Januar 2025</p>
          </div>

          <div className="bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-900 rounded-lg p-4 mb-6">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              <strong>Hinweis:</strong> Dies ist eine Vorlage. Bitte passen Sie die markierten Platzhalter [IN KLAMMERN] mit Ihren tatsächlichen Unternehmensdaten an und lassen Sie die Datenschutzerklärung von einem Rechtsanwalt prüfen.
            </p>
          </div>

          <div className="prose prose-slate dark:prose-invert max-w-none space-y-6">
            <section className="space-y-4">
              <h2 className="text-2xl font-semibold">1. Datenschutz auf einen Blick</h2>
              <h3 className="text-xl font-medium">Allgemeine Hinweise</h3>
              <p className="text-muted-foreground leading-relaxed">
                Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren 
                personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene 
                Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können. 
                Ausführliche Informationen zum Thema Datenschutz entnehmen Sie unserer unter diesem 
                Text aufgeführten Datenschutzerklärung.
              </p>

              <h3 className="text-xl font-medium">Datenerfassung auf dieser Website</h3>
              <p className="text-muted-foreground leading-relaxed font-medium">
                Wer ist verantwortlich für die Datenerfassung auf dieser Website?
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen 
                Kontaktdaten können Sie dem Abschnitt „Hinweis zur verantwortlichen Stelle" in dieser 
                Datenschutzerklärung entnehmen.
              </p>

              <p className="text-muted-foreground leading-relaxed font-medium">
                Wie erfassen wir Ihre Daten?
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei 
                kann es sich z.B. um Daten handeln, die Sie bei der Registrierung oder in einem 
                Kontaktformular eingeben. Andere Daten werden automatisch oder nach Ihrer Einwilligung 
                beim Besuch der Website durch unsere IT-Systeme erfasst. Das sind vor allem technische 
                Daten (z.B. Internetbrowser, Betriebssystem oder Uhrzeit des Seitenaufrufs).
              </p>

              <p className="text-muted-foreground leading-relaxed font-medium">
                Wofür nutzen wir Ihre Daten?
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu 
                gewährleisten. Andere Daten können zur Analyse Ihres Nutzerverhaltens verwendet werden, 
                um unseren Service zu verbessern.
              </p>

              <p className="text-muted-foreground leading-relaxed font-medium">
                Welche Rechte haben Sie bezüglich Ihrer Daten?
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und 
                Zweck Ihrer gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein 
                Recht, die Berichtigung oder Löschung dieser Daten zu verlangen. Wenn Sie eine 
                Einwilligung zur Datenverarbeitung erteilt haben, können Sie diese Einwilligung 
                jederzeit für die Zukunft widerrufen. Außerdem haben Sie das Recht, unter bestimmten 
                Umständen die Einschränkung der Verarbeitung Ihrer personenbezogenen Daten zu verlangen.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-semibold">2. Hosting</h2>
              <h3 className="text-xl font-medium">Supabase</h3>
              <p className="text-muted-foreground leading-relaxed">
                Wir hosten unsere Website und Datenbank bei Supabase (Anbieter: Supabase Inc., 
                970 Toa Payoh North #07-04, Singapore 318992). Wenn Sie unsere Website besuchen, 
                werden Ihre personenbezogenen Daten auf den Servern von Supabase verarbeitet. 
                Dabei können auch personenbezogene Daten an das Mutterunternehmen von Supabase in 
                den USA übermittelt werden.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Die Verwendung von Supabase erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO. 
                Wir haben ein berechtigtes Interesse an einer möglichst zuverlässigen Darstellung 
                unserer Website. Sofern eine entsprechende Einwilligung abgefragt wurde, erfolgt 
                die Verarbeitung ausschließlich auf Grundlage von Art. 6 Abs. 1 lit. a DSGVO; die 
                Einwilligung ist jederzeit widerrufbar.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Weitere Informationen entnehmen Sie der Datenschutzerklärung von Supabase: 
                <a 
                  href="https://supabase.com/privacy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline ml-1"
                >
                  https://supabase.com/privacy
                </a>
              </p>

              <h3 className="text-xl font-medium mt-6">Auftragsverarbeitung</h3>
              <p className="text-muted-foreground leading-relaxed">
                Wir haben einen Vertrag über Auftragsverarbeitung (AVV) zur Nutzung des oben 
                genannten Dienstes geschlossen. Hierbei handelt es sich um einen datenschutzrechtlich 
                vorgeschriebenen Vertrag, der gewährleistet, dass dieser die personenbezogenen Daten 
                unserer Websitebesucher nur nach unseren Weisungen und unter Einhaltung der DSGVO 
                verarbeitet.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-semibold">3. Allgemeine Hinweise und Pflichtinformationen</h2>
              <h3 className="text-xl font-medium">Datenschutz</h3>
              <p className="text-muted-foreground leading-relaxed">
                Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. 
                Wir behandeln Ihre personenbezogenen Daten vertraulich und entsprechend den 
                gesetzlichen Datenschutzvorschriften sowie dieser Datenschutzerklärung.
              </p>

              <h3 className="text-xl font-medium mt-6">Hinweis zur verantwortlichen Stelle</h3>
              <p className="text-muted-foreground leading-relaxed">
                Die verantwortliche Stelle für die Datenverarbeitung auf dieser Website ist:
              </p>
              <div className="bg-muted/50 rounded-lg p-4 text-muted-foreground space-y-1">
                <p>[FIRMENNAME]</p>
                <p>[STRASSE UND HAUSNUMMER]</p>
                <p>[PLZ UND ORT]</p>
                <p className="pt-2">Telefon: [TELEFONNUMMER]</p>
                <p>E-Mail: [E-MAIL-ADRESSE]</p>
              </div>

              <h3 className="text-xl font-medium mt-6">Speicherdauer</h3>
              <p className="text-muted-foreground leading-relaxed">
                Soweit innerhalb dieser Datenschutzerklärung keine speziellere Speicherdauer genannt 
                wurde, verbleiben Ihre personenbezogenen Daten bei uns, bis der Zweck für die 
                Datenverarbeitung entfällt. Wenn Sie ein berechtigtes Löschersuchen geltend machen 
                oder eine Einwilligung zur Datenverarbeitung widerrufen, werden Ihre Daten gelöscht, 
                sofern wir keine anderen rechtlich zulässigen Gründe für die Speicherung Ihrer 
                personenbezogenen Daten haben.
              </p>

              <h3 className="text-xl font-medium mt-6">Widerruf Ihrer Einwilligung zur Datenverarbeitung</h3>
              <p className="text-muted-foreground leading-relaxed">
                Viele Datenverarbeitungsvorgänge sind nur mit Ihrer ausdrücklichen Einwilligung 
                möglich. Sie können eine bereits erteilte Einwilligung jederzeit widerrufen. Die 
                Rechtmäßigkeit der bis zum Widerruf erfolgten Datenverarbeitung bleibt vom Widerruf 
                unberührt.
              </p>

              <h3 className="text-xl font-medium mt-6">Beschwerderecht bei der zuständigen Aufsichtsbehörde</h3>
              <p className="text-muted-foreground leading-relaxed">
                Im Falle von Verstößen gegen die DSGVO steht den Betroffenen ein Beschwerderecht bei 
                einer Aufsichtsbehörde, insbesondere in dem Mitgliedstaat ihres gewöhnlichen 
                Aufenthalts, ihres Arbeitsplatzes oder des Orts des mutmaßlichen Verstoßes zu.
              </p>
            </section>

            <section className="space-y-4" id="cookies">
              <h2 className="text-2xl font-semibold">4. Datenerfassung auf dieser Website</h2>
              <h3 className="text-xl font-medium">Cookies</h3>
              <p className="text-muted-foreground leading-relaxed">
                Unsere Internetseiten verwenden so genannte „Cookies". Cookies sind kleine 
                Datenpakete und richten auf Ihrem Endgerät keinen Schaden an. Sie werden entweder 
                vorübergehend für die Dauer einer Sitzung (Session-Cookies) oder dauerhaft 
                (permanente Cookies) auf Ihrem Endgerät gespeichert. Session-Cookies werden nach 
                Ende Ihres Besuchs automatisch gelöscht. Permanente Cookies bleiben auf Ihrem 
                Endgerät gespeichert, bis Sie diese selbst löschen oder eine automatische Löschung 
                durch Ihren Webbrowser erfolgt.
              </p>

              <Card className="p-6 my-6">
                <div className="space-y-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h4 className="text-lg font-semibold mb-2">Ihre Cookie-Einstellungen</h4>
                      <p className="text-sm text-muted-foreground mb-4">
                        Verwalten Sie Ihre Cookie-Präferenzen und ändern Sie Ihre Einstellungen jederzeit.
                      </p>
                    </div>
                    <Settings className="h-5 w-5 text-muted-foreground" />
                  </div>

                  {cookiePreferences ? (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">Ihre aktuellen Einstellungen:</p>
                      <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                        <li>• Notwendige Cookies: <span className="text-foreground font-medium">Aktiv</span></li>
                        <li>• Funktionale Cookies: <span className={cookiePreferences.functional ? "text-foreground font-medium" : ""}>
                          {cookiePreferences.functional ? "Aktiv" : "Inaktiv"}
                        </span></li>
                        <li>• Marketing-Cookies: <span className={cookiePreferences.marketing ? "text-foreground font-medium" : ""}>
                          {cookiePreferences.marketing ? "Aktiv" : "Inaktiv"}
                        </span></li>
                      </ul>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Sie haben Ihre Cookie-Präferenzen noch nicht festgelegt.
                    </p>
                  )}

                  <Button 
                    onClick={openCookieSettings}
                    variant="outline"
                    className="w-full sm:w-auto"
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Cookie-Einstellungen ändern
                  </Button>
                </div>
              </Card>

              <h4 className="text-lg font-medium mt-6">Cookie-Kategorien</h4>
              
              <div className="space-y-4 mt-4">
                <div className="border-l-4 border-primary pl-4">
                  <h5 className="font-medium mb-1">Notwendige Cookies</h5>
                  <p className="text-sm text-muted-foreground">
                    Diese Cookies sind für den Betrieb der Website unbedingt erforderlich und können in 
                    unseren Systemen nicht deaktiviert werden. Sie werden in der Regel nur als Reaktion 
                    auf von Ihnen getätigte Aktionen gesetzt, die einer Serviceanforderung entsprechen, 
                    wie etwa dem Festlegen Ihrer Datenschutzeinstellungen, dem Anmelden oder dem Ausfüllen 
                    von Formularen.
                  </p>
                </div>

                <div className="border-l-4 border-muted pl-4">
                  <h5 className="font-medium mb-1">Funktionale Cookies</h5>
                  <p className="text-sm text-muted-foreground">
                    Diese Cookies ermöglichen es der Website, erweiterte Funktionen und Personalisierung 
                    bereitzustellen. Sie können von uns oder von Drittanbietern gesetzt werden, deren 
                    Dienste wir auf unseren Seiten verwenden. Wenn Sie diese Cookies nicht zulassen, 
                    funktionieren einige oder alle dieser Dienste möglicherweise nicht ordnungsgemäß.
                  </p>
                </div>

                <div className="border-l-4 border-muted pl-4">
                  <h5 className="font-medium mb-1">Marketing-Cookies</h5>
                  <p className="text-sm text-muted-foreground">
                    Diese Cookies können über unsere Website von unseren Werbepartnern gesetzt werden. 
                    Sie können von diesen Unternehmen verwendet werden, um ein Profil Ihrer Interessen 
                    zu erstellen und Ihnen relevante Anzeigen auf anderen Websites zu zeigen. Sie 
                    speichern nicht direkt personenbezogene Daten, basieren jedoch auf einer eindeutigen 
                    Identifizierung Ihres Browsers und Internet-Geräts.
                  </p>
                </div>
              </div>

              <p className="text-muted-foreground leading-relaxed mt-6">
                Die Rechtsgrundlage für die Verwendung von Cookies ist Art. 6 Abs. 1 lit. f DSGVO 
                (berechtigtes Interesse) für technisch notwendige Cookies und Art. 6 Abs. 1 lit. a 
                DSGVO (Einwilligung) für alle anderen Cookie-Kategorien. Sie können Ihre Einwilligung 
                jederzeit über den Button "Cookie-Einstellungen ändern" widerrufen.
              </p>

              <h3 className="text-xl font-medium mt-6">Anfrage per E-Mail oder Telefon</h3>
              <p className="text-muted-foreground leading-relaxed">
                Wenn Sie uns per E-Mail oder Telefon kontaktieren, wird Ihre Anfrage inklusive aller 
                daraus hervorgehenden personenbezogenen Daten (Name, Anfrage) zum Zwecke der 
                Bearbeitung Ihres Anliegens bei uns gespeichert und verarbeitet. Diese Daten geben 
                wir nicht ohne Ihre Einwilligung weiter.
              </p>

              <h3 className="text-xl font-medium mt-6">Registrierung auf dieser Website</h3>
              <p className="text-muted-foreground leading-relaxed">
                Sie können sich auf dieser Website registrieren, um zusätzliche Funktionen zu nutzen. 
                Die dazu eingegebenen Daten verwenden wir nur zum Zwecke der Nutzung des jeweiligen 
                Angebotes oder Dienstes, für den Sie sich registriert haben. Die bei der Registrierung 
                abgefragten Pflichtangaben müssen vollständig angegeben werden. Anderenfalls werden 
                wir die Registrierung ablehnen.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Für wichtige Änderungen etwa beim Angebotsumfang oder bei technisch notwendigen 
                Änderungen nutzen wir die bei der Registrierung angegebene E-Mail-Adresse, um Sie 
                auf diesem Wege zu informieren.
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-semibold">5. Soziale Medien</h2>
              <p className="text-muted-foreground leading-relaxed">
                [FÜGEN SIE HIER INFORMATIONEN ZU VERWENDETEN SOCIAL-MEDIA-PLUGINS EIN, FALLS ZUTREFFEND]
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-semibold">6. Analyse-Tools und Werbung</h2>
              <p className="text-muted-foreground leading-relaxed">
                [FÜGEN SIE HIER INFORMATIONEN ZU VERWENDETEN ANALYSE-TOOLS WIE GOOGLE ANALYTICS EIN, FALLS ZUTREFFEND]
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-semibold">7. Newsletter</h2>
              <p className="text-muted-foreground leading-relaxed">
                [FÜGEN SIE HIER INFORMATIONEN ZU IHREM NEWSLETTER-SERVICE EIN, FALLS ZUTREFFEND]
              </p>
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-semibold">8. Plugins und Tools</h2>
              <h3 className="text-xl font-medium">Resend (E-Mail-Versand)</h3>
              <p className="text-muted-foreground leading-relaxed">
                Wir nutzen Resend für den Versand von E-Mails (z.B. Team-Einladungen). Anbieter ist 
                Resend Inc., 651 N Broad St, Suite 201, Middletown, Delaware 19709, USA.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Weitere Informationen entnehmen Sie der Datenschutzerklärung von Resend: 
                <a 
                  href="https://resend.com/legal/privacy-policy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline ml-1"
                >
                  https://resend.com/legal/privacy-policy
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
